import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  Search,
  Building2,
  Mail,
  MoreVertical,
  Pencil,
  Trash2,
  User,
  Monitor,
  Megaphone,
  ImageIcon,
  Eye,
  Settings,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';

export default function ClientsPage() {
  const { user, isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [screens, setScreens] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
  });
  
  // Assignment data
  const [assignedScreens, setAssignedScreens] = useState([]);
  const [assignedCampaigns, setAssignedCampaigns] = useState([]);
  const [assignedContent, setAssignedContent] = useState([]);
  const [assignTab, setAssignTab] = useState('screens');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, screensRes, campaignsRes, contentRes] = await Promise.all([
        api.get('/users'),
        api.get('/screens'),
        api.get('/campaigns'),
        api.get('/content'),
      ]);
      
      // Filter only clients
      const clientUsers = usersRes.data.filter(u => u.role === 'client');
      setClients(clientUsers);
      setScreens(screensRes.data);
      setCampaigns(campaignsRes.data);
      setContent(contentRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        // Update existing client
        await api.put(`/users/${editingClient.id}`, {
          name: formData.name,
          company: formData.company,
        });
        toast.success('Client updated successfully');
      } else {
        // Create new client with default client permissions
        await api.post('/users', {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: 'client',
          company: formData.company,
          permissions: ['dashboard', 'screens', 'campaigns', 'content', 'analytics', 'billing'],
          assigned_screens: [],
          assigned_campaigns: [],
          assigned_content: [],
        });
        toast.success('Client created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      email: client.email,
      password: '',
      name: client.name,
      company: client.company || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await api.delete(`/users/${clientId}`);
      toast.success('Client deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleOpenAssign = (client) => {
    setSelectedClient(client);
    setAssignedScreens(client.assigned_screens || []);
    setAssignedCampaigns(client.assigned_campaigns || []);
    setAssignedContent(client.assigned_content || []);
    setAssignTab('screens');
    setAssignDialogOpen(true);
  };

  const handleViewAssigned = (client) => {
    setSelectedClient(client);
    setViewDialogOpen(true);
  };

  const handleSaveAssignments = async () => {
    try {
      await api.put(`/users/${selectedClient.id}/assignments`, {
        assigned_screens: assignedScreens,
        assigned_campaigns: assignedCampaigns,
        assigned_content: assignedContent,
      });
      toast.success('Assignments saved successfully');
      setAssignDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save assignments');
    }
  };

  const toggleScreen = (screenId) => {
    setAssignedScreens(prev => 
      prev.includes(screenId) 
        ? prev.filter(id => id !== screenId)
        : [...prev, screenId]
    );
  };

  const toggleCampaign = (campaignId) => {
    setAssignedCampaigns(prev => 
      prev.includes(campaignId) 
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const toggleContent = (contentId) => {
    setAssignedContent(prev => 
      prev.includes(contentId) 
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    );
  };

  const selectAllScreens = () => setAssignedScreens(screens.map(s => s.id));
  const clearAllScreens = () => setAssignedScreens([]);
  const selectAllCampaigns = () => setAssignedCampaigns(campaigns.map(c => c.id));
  const clearAllCampaigns = () => setAssignedCampaigns([]);
  const selectAllContent = () => setAssignedContent(content.map(c => c.id));
  const clearAllContent = () => setAssignedContent([]);

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      company: '',
    });
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAssignmentCounts = (client) => ({
    screens: (client.assigned_screens || []).length,
    campaigns: (client.assigned_campaigns || []).length,
    content: (client.assigned_content || []).length,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin && user?.role !== 'operator') {
    return (
      <div className="glass-card rounded-xl p-12 text-center">
        <Users className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <h3 className="text-xl font-semibold text-white mb-2">Access Restricted</h3>
        <p className="text-zinc-400">Only administrators and operators can manage clients</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Client Management
          </h1>
          <p className="text-zinc-400 mt-1">Create clients and assign screens, campaigns & content they can access</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadData}
            className="border-zinc-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-500 btn-glow"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Total Clients</p>
                <p className="text-white text-2xl font-bold">{clients.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Monitor className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Available Screens</p>
                <p className="text-white text-2xl font-bold">{screens.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Megaphone className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Available Campaigns</p>
                <p className="text-white text-2xl font-bold">{campaigns.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <ImageIcon className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Available Content</p>
                <p className="text-white text-2xl font-bold">{content.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <Input
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800 text-white"
          data-testid="search-clients"
        />
      </div>

      {/* Clients Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            Client Accounts ({filteredClients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">No clients found</p>
              <p className="text-zinc-500 text-sm mt-1">Add your first client to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Client</TableHead>
                  <TableHead className="text-zinc-500">Company</TableHead>
                  <TableHead className="text-zinc-500">Assigned Resources</TableHead>
                  <TableHead className="text-zinc-500">Created</TableHead>
                  <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => {
                  const counts = getAssignmentCounts(client);
                  return (
                    <TableRow key={client.id} className="border-zinc-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{client.name}</p>
                            <p className="text-zinc-500 text-sm">{client.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {client.company || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Badge variant="outline" className={counts.screens > 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-zinc-800 text-zinc-500 border-zinc-700'}>
                            <Monitor className="w-3 h-3 mr-1" />
                            {counts.screens} Screens
                          </Badge>
                          <Badge variant="outline" className={counts.campaigns > 0 ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-zinc-800 text-zinc-500 border-zinc-700'}>
                            <Megaphone className="w-3 h-3 mr-1" />
                            {counts.campaigns} Campaigns
                          </Badge>
                          <Badge variant="outline" className={counts.content > 0 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-zinc-800 text-zinc-500 border-zinc-700'}>
                            <ImageIcon className="w-3 h-3 mr-1" />
                            {counts.content} Content
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-400">
                        {client.created_at ? format(new Date(client.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewAssigned(client)}
                            className="border-zinc-700"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleOpenAssign(client)}
                            className="bg-blue-600 hover:bg-blue-500"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Assign
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4 text-zinc-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                              <DropdownMenuItem
                                onClick={() => handleEdit(client)}
                                className="text-white hover:bg-zinc-800"
                              >
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(client.id)}
                                className="text-red-400 hover:bg-zinc-800"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Client
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Client name"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-300">Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              {!editingClient && (
                <>
                  <div>
                    <Label className="text-zinc-300">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="client@example.com"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      required
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                {editingClient ? 'Update Client' : 'Create Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Resources Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Assign Resources to {selectedClient?.name}
            </DialogTitle>
            <p className="text-zinc-400 text-sm">
              Select which screens, campaigns, and content this client can access
            </p>
          </DialogHeader>
          
          <Tabs value={assignTab} onValueChange={setAssignTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="bg-zinc-800 border border-zinc-700">
              <TabsTrigger value="screens" className="data-[state=active]:bg-blue-600">
                <Monitor className="w-4 h-4 mr-2" />
                Screens ({assignedScreens.length}/{screens.length})
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-blue-600">
                <Megaphone className="w-4 h-4 mr-2" />
                Campaigns ({assignedCampaigns.length}/{campaigns.length})
              </TabsTrigger>
              <TabsTrigger value="content" className="data-[state=active]:bg-blue-600">
                <ImageIcon className="w-4 h-4 mr-2" />
                Content ({assignedContent.length}/{content.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="screens" className="flex-1 overflow-auto mt-4">
              <div className="flex justify-between mb-3">
                <Button size="sm" variant="outline" onClick={selectAllScreens} className="border-zinc-700">
                  Select All
                </Button>
                <Button size="sm" variant="outline" onClick={clearAllScreens} className="border-zinc-700">
                  Clear All
                </Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {screens.map((screen) => (
                  <div
                    key={screen.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      assignedScreens.includes(screen.id)
                        ? 'bg-emerald-500/10 border-emerald-500/30'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                    onClick={() => toggleScreen(screen.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={assignedScreens.includes(screen.id)}
                        onCheckedChange={() => toggleScreen(screen.id)}
                      />
                      <div>
                        <p className="text-white font-medium">{screen.name}</p>
                        <p className="text-zinc-500 text-sm">{screen.location}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700">
                      {screen.resolution}
                    </Badge>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="campaigns" className="flex-1 overflow-auto mt-4">
              <div className="flex justify-between mb-3">
                <Button size="sm" variant="outline" onClick={selectAllCampaigns} className="border-zinc-700">
                  Select All
                </Button>
                <Button size="sm" variant="outline" onClick={clearAllCampaigns} className="border-zinc-700">
                  Clear All
                </Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      assignedCampaigns.includes(campaign.id)
                        ? 'bg-purple-500/10 border-purple-500/30'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                    onClick={() => toggleCampaign(campaign.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={assignedCampaigns.includes(campaign.id)}
                        onCheckedChange={() => toggleCampaign(campaign.id)}
                      />
                      <div>
                        <p className="text-white font-medium">{campaign.name}</p>
                        <p className="text-zinc-500 text-sm">{campaign.client_name || 'No client'}</p>
                      </div>
                    </div>
                    <Badge className={
                      campaign.status === 'active' 
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-zinc-700 text-zinc-400 border-zinc-600'
                    }>
                      {campaign.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="content" className="flex-1 overflow-auto mt-4">
              <div className="flex justify-between mb-3">
                <Button size="sm" variant="outline" onClick={selectAllContent} className="border-zinc-700">
                  Select All
                </Button>
                <Button size="sm" variant="outline" onClick={clearAllContent} className="border-zinc-700">
                  Clear All
                </Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {content.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      assignedContent.includes(item.id)
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                    onClick={() => toggleContent(item.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={assignedContent.includes(item.id)}
                        onCheckedChange={() => toggleContent(item.id)}
                      />
                      <div>
                        <p className="text-white font-medium">{item.name}</p>
                        <p className="text-zinc-500 text-sm">{item.type}</p>
                      </div>
                    </div>
                    <Badge className={
                      item.status === 'approved' 
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-zinc-700 text-zinc-400 border-zinc-600'
                    }>
                      {item.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex items-center gap-4 mr-auto">
              <span className="text-zinc-400 text-sm">
                Selected: {assignedScreens.length} screens, {assignedCampaigns.length} campaigns, {assignedContent.length} content
              </span>
            </div>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSaveAssignments} className="bg-blue-600 hover:bg-blue-500">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Save Assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Assigned Resources Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              {selectedClient?.name}'s Assigned Resources
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Assigned Screens */}
            <div>
              <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                <Monitor className="w-4 h-4 text-emerald-400" />
                Assigned Screens ({(selectedClient?.assigned_screens || []).length})
              </h4>
              {(selectedClient?.assigned_screens || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(selectedClient?.assigned_screens || []).map(screenId => {
                    const screen = screens.find(s => s.id === screenId);
                    return screen ? (
                      <Badge key={screenId} className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                        {screen.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No screens assigned</p>
              )}
            </div>

            {/* Assigned Campaigns */}
            <div>
              <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                <Megaphone className="w-4 h-4 text-purple-400" />
                Assigned Campaigns ({(selectedClient?.assigned_campaigns || []).length})
              </h4>
              {(selectedClient?.assigned_campaigns || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(selectedClient?.assigned_campaigns || []).map(campaignId => {
                    const campaign = campaigns.find(c => c.id === campaignId);
                    return campaign ? (
                      <Badge key={campaignId} className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                        {campaign.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No campaigns assigned</p>
              )}
            </div>

            {/* Assigned Content */}
            <div>
              <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                <ImageIcon className="w-4 h-4 text-amber-400" />
                Assigned Content ({(selectedClient?.assigned_content || []).length})
              </h4>
              {(selectedClient?.assigned_content || []).length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(selectedClient?.assigned_content || []).map(contentId => {
                    const item = content.find(c => c.id === contentId);
                    return item ? (
                      <Badge key={contentId} className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                        {item.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No content assigned</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-zinc-700">
              Close
            </Button>
            <Button onClick={() => { setViewDialogOpen(false); handleOpenAssign(selectedClient); }} className="bg-blue-600 hover:bg-blue-500">
              <Settings className="w-4 h-4 mr-2" />
              Edit Assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
